/*
 * Copyright © 2023 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React from 'react';
import { Box, Grid, Paper, Typography, useMediaQuery, useTheme } from '@material-ui/core';
import LinearProgress from '@material-ui/core/LinearProgress';
import { makeStyles } from '@material-ui/core/styles';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import FileError from './error/FileError';
import Button from '../../../common/Button';
import DropZone from '../../../dropzone/DropZone';
import { getFileRejectionMessage } from '../../../../shared/utils/attachmentUtils';
import { showAlertError } from '../../../../redux/features/alerts/alerts.slice';
import { createMessage } from '../../../../redux/features/alerts/alerts.helper';
import AttachmentSubTypeSelector from './AttachmentSubTypeSelector';
import AttachmentMetaFields from './meta/AttachmentMetaFields';
import { useAttachmentUploader } from './useAttachmentUploader';
import { getAttachmentConfigValue, MAX_FILE_SIZE } from '../services/attachments.helper';
import { ATTACHMENT_CONFIG_ATTRIBUTES } from '../services/constants';

const useStyles = makeStyles(theme => ({
  titleText: {
    color: theme.palette.titleText,
    fontSize: '1.25rem',
    fontWeight: 600,
  },
  subtitleText: {
    color: theme.palette.titleText,
    fontWeight: 600,
    marginBottom: 0,
  },
  buttonGroup: {
    marginTop: theme.spacing(2),
  },
  boxIndent: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  formGroup: {
    marginTop: theme.spacing(3),
  },
  dropzone: {
    marginTop: theme.spacing(1),
  },
  technicalField: {
    marginBottom: theme.spacing(1),
    width: '19rem',
  },
  saTechnicalField: {
    marginBottom: theme.spacing(1),
    width: '23rem',
  },
  additionalField: {
    marginBottom: theme.spacing(1),
    minWidth: '100%',
  },
  hidden: {
    display: 'none',
  },
}));

/**
 * Attachment uploader control that allows selecting any subtype for the given attachmentType
 * and adding metadata specific to each subtype.
 *
 * @param draftId parent submission id
 * @param attachmentType root attachment type
 * @param metaFieldNames array with all the meta fields of the attachment type (can be undefined)
 * @param uiSchema current attachment's field uiSchema config
 * @param jsonSchema root jsonSchema
 * @param titleLabel uploader title text
 * @param subtitleLabel uploader subtitle text
 * @param selectorLabel selector label text
 * @param parentClasses form parent classes
 * @param readOnly form read only mode
 * @param attachmentsMetadata submission attachments data for the current type
 * @param mergedAttachmentsMetadata uploaded and dropped attachments data
 * @param onAttachmentUploaded handler for uploaded attachment
 * @param onFileDropped handler for file dropped
 * @param onDroppedFileRemoved handler for dropped file removed
 * @returns {JSX.Element|null}
 * @constructor
 */
export default function AttachmentUploader({
  draftId,
  attachmentType,
  metaFieldNames,
  uiSchema,
  jsonSchema,
  titleLabel,
  subtitleLabel,
  selectorLabel,
  confirmLabel,
  parentClasses,
  readOnly,
  attachmentsMetadata,
  mergedAttachmentsMetadata,
  onAttachmentUploaded,
  onFileDropped,
  onDroppedFileRemoved,
  onConfirmUploadArg,
}) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const classes = useStyles();

  const theme = useTheme();
  const isMediumOrBigger = useMediaQuery(theme.breakpoints.up('md'));

  const {
    metaForm,
    attachmentsConfigReady,
    attachmentSubtype,
    defaultAttachmentConfig,
    droppedFiles,
    onDropFile,
    onRemoveDroppedFile,
    areFilesDropped,
    isDropzoneDisabled,
    maxNoOfFilesUploaded,
    isUploaderTitleHidden,
    isUploaderSubtitleVisible,
    onConfirmUpload,
    isLoading,
    progress,
    fileErrorMsg,
    closeAlert,
  } = useAttachmentUploader({
    draftId,
    attachmentsUiSchema: uiSchema,
    attachmentType,
    metaFieldNames,
    attachmentsMetadata,
    mergedAttachmentsMetadata,
    onAttachmentUploaded,
    onFileDropped,
    onDroppedFileRemoved,
  });

  if (!readOnly && attachmentsConfigReady && !maxNoOfFilesUploaded()) {
    return (
      <>
        <Paper className={parentClasses.paper}>
          <Grid container spacing={1}>
            <Grid item xs={12} md={12}>
              <>
                {!isUploaderTitleHidden && (
                  <Typography
                    data-testid={`${attachmentType}-title`}
                    className={isUploaderTitleHidden ? classes.hidden : classes.titleText}
                  >
                    {titleLabel}
                  </Typography>
                )}

                {isUploaderSubtitleVisible && (
                  <Grid container direction="row" alignItems="center">
                    <Typography variant="subtitle1" component="span" gutterBottom className={classes.subtitleText}>
                      {subtitleLabel}
                    </Typography>
                  </Grid>
                )}

                <AttachmentSubTypeSelector
                  attachmentType={attachmentType}
                  attachmentsUiSchema={uiSchema}
                  uploadedAttachmentsMetadata={attachmentsMetadata}
                  allAttachmentsMetadata={mergedAttachmentsMetadata}
                  metaForm={metaForm}
                  typeLabel={isMediumOrBigger ? selectorLabel : t('select.option')}
                  rjsfFormClasses={parentClasses}
                  uploaderClasses={classes}
                  jsonSchema={jsonSchema}
                />

                <AttachmentMetaFields
                  attachmentType={attachmentType}
                  attachmentSubType={attachmentSubtype}
                  classes={classes}
                  readOnly={readOnly}
                  metaForm={metaForm}
                  allAttachmentsMetadata={mergedAttachmentsMetadata}
                  parentClasses={parentClasses}
                  attachmentsUiSchema={uiSchema}
                  jsonSchema={jsonSchema}
                />
              </>
            </Grid>
          </Grid>

          <FileError errorMsgs={fileErrorMsg} onClose={closeAlert} />

          <Grid container spacing={1} className={classes.dropzone}>
            <Grid item xs={12} md={12}>
              <DropZone
                id={`${attachmentType}-dropzone`}
                filesLimit={1}
                item={droppedFiles}
                maxFileSize={MAX_FILE_SIZE}
                acceptedMimeTypes={getAttachmentConfigValue(
                  defaultAttachmentConfig,
                  ATTACHMENT_CONFIG_ATTRIBUTES.ACCEPTED_MIME_TYPES,
                )}
                readOnly={readOnly || isLoading}
                onDeletePreviewFile={onRemoveDroppedFile}
                showPreviews={areFilesDropped()}
                onDrop={onDropFile}
                disabled={isDropzoneDisabled() || isLoading}
                onDropRejected={([rejectedFile]) => {
                  const fileRejectionMessageProps = {
                    rejectedFile,
                    acceptedMimeTypes: getAttachmentConfigValue(
                      defaultAttachmentConfig,
                      ATTACHMENT_CONFIG_ATTRIBUTES.ACCEPTED_MIME_TYPES,
                    ),
                    acceptedFileExtensions: getAttachmentConfigValue(
                      defaultAttachmentConfig,
                      ATTACHMENT_CONFIG_ATTRIBUTES.ACCEPTED_FILE_EXTENSIONS,
                    ),
                    maxFileSize: MAX_FILE_SIZE,
                    existingFiles: droppedFiles,
                  };
                  dispatch(showAlertError(createMessage(getFileRejectionMessage(fileRejectionMessageProps))));
                }}
              />
              {isLoading && progress === 100 && <LinearProgress />}
              {isLoading && progress < 100 && <LinearProgress variant="determinate" value={progress} />}
            </Grid>

            <Grid container>
              <Grid item xs={12}>
                <Box display="flex" justifyContent="flex-end">
                  <div className={classes.buttonGroup}>
                    <Button
                      onClick={() =>
                        onConfirmUploadArg
                          ? onConfirmUploadArg(droppedFiles, onRemoveDroppedFile)
                          : onConfirmUpload(jsonSchema)
                      }
                      color="primary"
                      id={`${attachmentType}-dropzone-confirmUpload`}
                      disabled={!areFilesDropped() || isLoading}
                    >
                      {t(confirmLabel || 'attachments.confirm.upload')}
                    </Button>
                  </div>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Paper>
      </>
    );
  }
  return null;
}
